'use client';

type PhotoListImage = {
  id: number;
  imagePath: string;
  imageType: string;
  caption?: string;
};
import styles from './PhotoList.module.scss';
import '@splidejs/react-splide/css';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import Image from 'next/image';
import { useEffect, useRef } from 'react';

function PhotoList({
  images,
  activeIndex,
  onImageClick,
  compact = false,
}: {
  images: PhotoListImage[];
  activeIndex: number;
  onImageClick: (imageIndex: number) => void;
  /** コンパクトモード: 親の幅に追従し、固定サイズを使わない（アイテム詳細セクション用） */
  compact?: boolean;
}) {
  const splideRef = useRef<Splide>(null);
  const thumbnailNavigationRef = useRef<HTMLDivElement>(null);

  // 外部からactiveIndexが変更された場合に反映
  useEffect(() => {
    if (splideRef.current) {
      splideRef.current.go(activeIndex);
    }
  }, [activeIndex]);

  // アクティブなサムネイルを自動スクロール
  useEffect(() => {
    if (thumbnailNavigationRef.current) {
      const activeButton = thumbnailNavigationRef.current.querySelector(
        `[data-thumbnail-index="${activeIndex}"]`,
      ) as HTMLElement;

      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeIndex]);

  // ナビゲーションクリック時
  const handleSlideClick = (index: number) => {
    onImageClick(index);
  };

  //スライダー移動時
  const handleSplideMove = (index: number) => {
    onImageClick(index);
  };

  // 前のスライドに移動する関数
  const handlePrevSlide = () => {
    if (splideRef.current) {
      splideRef.current.go('<');
    }
  };

  // 次のスライドに移動する関数
  const handleNextSlide = () => {
    if (splideRef.current) {
      splideRef.current.go('>');
    }
  };

  const options = {
    type: 'loop',
    perPage: 1,
    arrows: false,
    pagination: false,
    height: '100%',
  };

  const compactClass = compact ? styles.compact : '';

  return (
    <>
      <div className={`${styles.splideContainer} ${compactClass}`}>
        <div className={`${styles.splideImageTextContainer} ${compactClass}`}>
          {/* 固定の画像枠 - border-radiusを持つ要素は動かさない */}
          <div className={`${styles.splideImageWrapper} ${compactClass}`}>
            <div className={styles.splideWrapper}>
              <button type='button' className={styles.customPrevArrow} onClick={handlePrevSlide}>
                <RoundArrowIcon direction='left' color='black' size='large' bgColor='white' borderColor='black' />
              </button>
              <button type='button' className={styles.customNextArrow} onClick={handleNextSlide}>
                <RoundArrowIcon direction='right' color='black' size='large' bgColor='white' borderColor='black' />
              </button>
              <Splide
                options={options}
                ref={splideRef}
                onMove={({ index }) => handleSplideMove(index)}
                hasTrack={false}
                className={styles.splide}
              >
                <SplideTrack className={styles.splideTrack}>
                  {images.map((image, index) => (
                    <SplideSlide key={image.imagePath} className={styles.splideSlide}>
                      <Image
                        src={image.imagePath}
                        alt={image.imageType}
                        className={styles.splideImg}
                        width={compact ? 300 : 490}
                        height={compact ? 300 : 490}
                        priority={index < 1}
                        style={{ objectFit: 'contain', height: 'auto' }}
                        unoptimized={image.imagePath.startsWith('/api/')}
                      />
                    </SplideSlide>
                  ))}
                </SplideTrack>
              </Splide>
            </div>
          </div>
          {/* キャプションはcompact時は非表示 */}
          {!compact && (
            <div className={styles.splideTextArea}>
              <p className={styles.splideDescription}>{images[activeIndex]?.caption}</p>
            </div>
          )}
        </div>
      </div>
      <div className={`${styles.thumbnailNavigation} ${compactClass}`} ref={thumbnailNavigationRef}>
        {images.map((image, index) => (
          <div
            key={image.imagePath}
            onClick={() => handleSlideClick(index)}
            className={`${styles.navButton} ${compactClass} ${activeIndex === index ? styles.active : ''}`}
            data-thumbnail-index={index}
          >
            <Image
              src={image.imagePath}
              alt={image.imageType}
              className={`${styles.buttonImg} ${activeIndex === index ? styles.prevImg : ''}`}
              width={compact ? 40 : 54}
              height={compact ? 40 : 54}
              style={{ objectFit: 'contain' }}
              unoptimized={image.imagePath.startsWith('/api/')}
            />
          </div>
        ))}
      </div>
    </>
  );
}

export default PhotoList;
